import * as React from 'react';
import * as classnames from 'classnames';
import {
    AudienceRule as IAudienceRule,
    AudienceRuleOption,
    MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES,
    Project,
    QTYPES
} from '@nexxt/common/types';
import AudienceRuleHeader from '../AudienceRuleHeader';
import AudienceVectorMap from './AudienceVectorMap';
import { MULTIPLE_ANSWER_TYPES } from '../../../../../constants/question';
import { errorObject } from '../../AudienceRule';
import { BUILT_IN_OPTION_VALUE } from '@nexxt/common/constants';

interface BuiltInAudienceRuleProps {
    audienceRule: IAudienceRule;
    currentProject: Project;
    toggle: boolean;
    isDisabled: boolean;
    isMenuOpen: boolean;
    totalSampleSize: number;
    hasInvalid: boolean;
    errorObject: errorObject;
    health?: any;
    setIsMenuOpen: () => void;
    onEdit: (editingQuestion) => void;
    onDelete: () => void;
    onChangeFilterToggle: () => void;
    onChangeTargettingOption: (
        response: { label: string; value: string } | boolean,
        isMultipleAnswer: boolean,
        isBuiltIn: boolean,
        index: number
    ) => void;
    onChangeSampleSize: (
        id: number,
        index: number,
        event: React.ChangeEvent<HTMLInputElement>
    ) => void;
    onClickSelectAll: (selectAll: boolean, isMultipleAns: boolean) => void;
    disableByFieldName: (keyName?: string) => boolean;
}
const BuiltInAudienceRule = (props: BuiltInAudienceRuleProps): JSX.Element => {
    const { audienceRuleOptions, selectedQuestion } = props.audienceRule;

    const checkSelectAll = () => {
        let check;
        const filteredAudienceRuleOptions = audienceRuleOptions.filter(
            (audienceRuleOption) =>
                audienceRuleOption.value !== BUILT_IN_OPTION_VALUE
        );
        if (!isMultipleAns) {
            check = filteredAudienceRuleOptions?.some(
                (opt) => !opt.mode?.terminateEnabled
            );
        } else {
            check = filteredAudienceRuleOptions.some(
                (opt) =>
                    opt.mode?.ruleType ===
                    MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT
            );
        }
        return check ? false : true;
    };
    const isMultipleAns = MULTIPLE_ANSWER_TYPES.includes(
        selectedQuestion.type as QTYPES
    );
    // Not multiple ans -> terminateEnabled === false, option is selected
    // Is multiple ans -> ruleType === MUST_SELECT, option is selected
    const isSelected = (option: AudienceRuleOption) => {
        return isMultipleAns
            ? option.mode.ruleType ===
                  MULTIPLE_ANSWER_AUDIENCE_RULE_TYPES.MUST_SELECT
            : !option.mode.terminateEnabled;
    };

    const calculateUsedSampleSize = (): number => {
        if (props.toggle) {
            const totalUsedSampleSize = [...audienceRuleOptions].reduce(
                (total, option) => {
                    if (!option.mode.terminateEnabled) {
                        return total + option.sampleSize.max;
                    } else {
                        return total;
                    }
                },
                0
            );
            if (!totalUsedSampleSize) {
                return 0;
            } else {
                return totalUsedSampleSize;
            }
        }
        return 0;
    };

    const renderOptions = () =>
        audienceRuleOptions.map((option, index) => {
            const isSystemOption = option.value === BUILT_IN_OPTION_VALUE;
            return (
                <div
                    className={classnames(
                        `content-container--content--option`,
                        {
                            selected: isSelected(option),
                            isDisabled: props.isDisabled || isSystemOption,
                            isSystemOption: isSystemOption
                        }
                    )}
                    key={`${selectedQuestion.questionId}-${index}-content`}
                    onClick={() => {
                        !props.isDisabled &&
                            !isSystemOption &&
                            props.onChangeTargettingOption(
                                !isSelected(option),
                                isMultipleAns,
                                true,
                                index
                            );
                    }}
                >
                    {option.label}
                </div>
            );
        });

    const renderQuotaOptions = () => {
        const filteredOptions: any = [...audienceRuleOptions].filter(
            (opt, index) => {
                if (isSelected(opt)) {
                    opt['index'] = index;
                    return opt;
                }
            }
        );
        return filteredOptions.map((option, index) => {
            return (
                <tr
                    className="option-row"
                    key={`${selectedQuestion.questionId}-${index}-option`}
                >
                    <td>
                        <span>{option.label}</span>
                    </td>
                    {props.toggle && isSelected(option) && (
                        <td align="right">
                            <input
                                type="number"
                                name="max"
                                min={0}
                                className={`form-group__control ${
                                    props.hasInvalid &&
                                    props.errorObject.optionKeys[
                                        option.index
                                    ] &&
                                    'error-border'
                                }`}
                                value={option.sampleSize.max}
                                onChange={(e) =>
                                    props.onChangeSampleSize(
                                        option.id,
                                        option.index,
                                        e
                                    )
                                }
                                defaultValue=""
                                disabled={
                                    props.isDisabled ||
                                    option.mode.terminateEnabled
                                }
                                placeholder={
                                    props.hasInvalid &&
                                    props.errorObject.optionKeys[
                                        option.index
                                    ] &&
                                    'Enter Quota'
                                }
                                style={{
                                    width: 'auto'
                                }}
                            />
                        </td>
                    )}
                </tr>
            );
        });
    };
    const switchContent = () => {
        let isMapSupported = false;
        if (selectedQuestion.type === QTYPES.VECTOR_MAP) {
            const vectorMapSupportedCountries = ['CA', 'US'];
            const country = vectorMapSupportedCountries.find(
                (country) => country === props.currentProject.countries[0].value
            );
            country && (isMapSupported = true);
        }
        switch (true) {
            case selectedQuestion.type === QTYPES.VECTOR_MAP &&
                isMapSupported === true:
                return (
                    <div className="audience-vector-map-ui">
                        <AudienceVectorMap
                            totalSampleSize={props.totalSampleSize}
                            audienceRule={props.audienceRule}
                            countries={props.currentProject.countries}
                            toggle={props.toggle}
                            isDisabled={props.isDisabled}
                            isSelected={isSelected}
                            onChangeTargettingOption={
                                props.onChangeTargettingOption
                            }
                        />
                    </div>
                );
            default:
                return (
                    <div className="content-container--content">
                        {renderOptions()}
                    </div>
                );
        }
    };
    return (
        <>
            <AudienceRuleHeader
                audienceRule={props.audienceRule}
                toggle={props.toggle}
                quotaEnabled={!isMultipleAns}
                isDisabled={props.isDisabled}
                isMenuOpen={props.isMenuOpen}
                setIsMenuOpen={props.setIsMenuOpen}
                onEdit={props.onEdit}
                onDelete={props.onDelete}
                onChangeFilterToggle={props.onChangeFilterToggle}
                isBuiltIn={true}
                isHalfWidth={false}
                hasInvalid={props.hasInvalid}
                errorObject={props.errorObject}
                disableByFieldName={props.disableByFieldName}
                health={props.health}
            />
            <div
                className={`audience--screeners--card--built-in ${
                    props.isDisabled && 'isDisabled'
                }`}
            >
                <div className="content-container">
                    <div className="content-container--label">
                        <span>{selectedQuestion.label} Options</span>
                        <span
                            className={classnames('clk', {
                                disabled: props.isDisabled
                            })}
                            onClick={() => {
                                if (!props.isDisabled) {
                                    const check = checkSelectAll();
                                    props.onClickSelectAll(
                                        check,
                                        isMultipleAns
                                    );
                                }
                            }}
                        >
                            {checkSelectAll() ? 'Select All' : 'Unselect All'}
                        </span>
                    </div>
                    {switchContent()}
                </div>
                <div className="options-container">
                    <div className="options-container--label">
                        <span>Selected Options</span>
                    </div>
                    <div className="options-container--options">
                        <table>
                            <colgroup>
                                <>
                                    <col span={1} className="col-2" />
                                    <col span={1} className="col-2" />
                                </>
                            </colgroup>
                            <tbody>
                                <tr className="content-header">
                                    <td>Targeting Options</td>
                                    {props.toggle && (
                                        <td
                                            align="right"
                                            className={`${
                                                calculateUsedSampleSize() !==
                                                    props.totalSampleSize &&
                                                'error-text'
                                            }`}
                                        >
                                            Quota{' '}
                                            {`${calculateUsedSampleSize()}/${
                                                props.totalSampleSize
                                            }`}
                                        </td>
                                    )}
                                </tr>
                                {renderQuotaOptions()}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </>
    );
};
export default BuiltInAudienceRule;
